import type {
  SearchCategoriesSuccess,
  SearchDietaryOptionsSuccess,
  SearchPopularTagsSuccess,
  SearchTagsSuccess
} from '~/services/api/generated'
import type {
  SearchResponse,
  SearchSearchSuccessCodec,
  StoreSearchResult
} from '~/services/api/search/types'
import type {
  SearchCategoriesResponse,
  SearchDietaryOptions,
  SearchPopularTagsResponse,
  SearchTagsResponse
} from '~/services/api/search/interfaces'

type StoreSearchWireResult = SearchSearchSuccessCodec['data']['stores'][number]

// A null average_rating means no rating exists; review_count is forced to 0 alongside it so a
// wire inconsistency (a stray positive count with no rating) can never render a blank number.
const normalizeStore = (value: StoreSearchWireResult): StoreSearchResult => ({
  ...value,
  average_rating: value.average_rating,
  review_count: value.average_rating === null ? 0 : value.review_count,
  created_at: value.created_at ?? '',
  delivery_fee: value.delivery_fee,
  delivery_radius: value.delivery_radius,
  latitude: value.latitude,
  longitude: value.longitude,
  distance_miles: value.distance_miles ?? null,
  distance: value.distance_miles ?? null
})

export const storeSearchContract = Object.freeze({
  parse(validated: SearchSearchSuccessCodec): SearchResponse {
    const { data, ...rest } = validated
    const { stores, ...dataRest } = data
    return {
      ...rest,
      data: {
        ...dataRest,
        stores: stores.map(normalizeStore)
      }
    }
  },
  parseCategoriesResponse(validated: SearchCategoriesSuccess): SearchCategoriesResponse {
    return {
      categories: validated.data.categories.map(({ id, name, slug }) => ({ id, name, slug }))
    }
  },
  parseDietaryOptionsResponse(validated: SearchDietaryOptionsSuccess): SearchDietaryOptions {
    return {
      dietary_options: validated.data.dietaryOptions.map(({ id, name, slug }) => ({
        id,
        name,
        slug
      }))
    }
  },
  parsePopularTagsResponse(validated: SearchPopularTagsSuccess): SearchPopularTagsResponse {
    return {
      tags: validated.data.tags.map(({ description, id, name, slug }) => ({
        description,
        id,
        name,
        slug
      }))
    }
  },
  parseTagsResponse(validated: SearchTagsSuccess): SearchTagsResponse {
    return {
      tags: validated.data.groups.flatMap((group) => validated.data.tags[group] ?? [])
    }
  }
})
